import React, { useEffect, useRef } from 'react';
import styles from './PlayerTrackingCanvas.module.scss';


/**
 * PlayerTrackingCanvas renders player positions as paths on a canvas.
 * @param {Object[]} data - Array of tracking points {RallyID, player_side, frame, mid_x, mid_y}
 * @param {Object} [style] - Optional style for the canvas
 */
const PlayerTrackingCanvas = React.memo(function PlayerTrackingCanvas({
  data,
  hits = [],
  style,
  width = 500,
  height = 800,
  showLegend = true,
  showTitle = true,
  // Default colors: Player 1 (A) Red, Player 2 (B) Light Blue
  pathColors = { A: '#ef4444', B: '#60A5FA' },
  pathStrokeWidth = 2,
  endpointStrokeWidth = 2.5,
  netColor = '#4A5568',
  netLineWidthScale = 0.02,
  showNoDataMessage = true,
  pathShadow = true,
  shadowBlur = 6,
  // New controls for star density
  minStarDistanceMeters = 0.5,
  minStarFrameGap = 3
}) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const [canvasSize, setCanvasSize] = React.useState({ width, height });
  const [trackingLoading, setTrackingLoading] = React.useState(false);
  const [trackingError, setTrackingError] = React.useState(null);

  useEffect(() => {
    const node = containerRef.current;
    if (!node || typeof ResizeObserver === 'undefined') {
      setCanvasSize({ width, height });
      return;
    }

    const updateSize = () => {
      const nextWidth = Math.max(1, Math.round(node.clientWidth || width));
      const nextHeight = Math.max(1, Math.round(node.clientHeight || height));
      setCanvasSize((prev) => (
        prev.width === nextWidth && prev.height === nextHeight
          ? prev
          : { width: nextWidth, height: nextHeight }
      ));
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(node);

    return () => observer.disconnect();
  }, [width, height]);


  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    // Reset any previous transforms and clear canvas to avoid stacking between renders
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);


    // Streamlit-matching court geometry
    const court = {
      totalLength: 13.40,         // L
      totalWidth: 6.10,           // W
      singlesWidth: 5.18,         // Ws
      lineWidth: 0.04,
      shortServiceFromNet: 1.98,  // distance from net
      longServiceFromBack: 0.76,  // doubles long service line from back boundary
      singlesOffsetFromEdge: 0.46 // (W - Ws)/2
    };

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const gradient = ctx.createRadialGradient(
      centerX, centerY, 0,
      centerX, centerY, Math.max(centerX, centerY) * 1.3
    );
    gradient.addColorStop(0, '#22c55e'); // Vibrant vibrant green center
    gradient.addColorStop(0.6, '#15803d'); // Transition
    gradient.addColorStop(1, '#0b4d26'); // Deeper, more "lush" dark green at edges
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);


    // Determine the scale factor to fit the court within the canvas
    const courtAspectRatio = court.totalWidth / court.totalLength;
    const canvasAspectRatio = canvas.width / canvas.height;


    let scale, courtPixelWidth, courtPixelHeight, offsetX, offsetY;


    if (canvasAspectRatio > courtAspectRatio) {
      courtPixelHeight = canvas.height;
      scale = courtPixelHeight / court.totalLength;
      courtPixelWidth = scale * court.totalWidth;
    } else {
      courtPixelWidth = canvas.width;
      scale = courtPixelWidth / court.totalWidth;
      courtPixelHeight = scale * court.totalLength;
    }


    // Calculate offsets to center the court drawing on the canvas
    offsetX = (canvas.width - courtPixelWidth) / 2;
    offsetY = (canvas.height - courtPixelHeight) / 2;


    // Save context and translate to center the court
    ctx.save();
    ctx.translate(offsetX, offsetY);


    // --- Define Line Styles ---
    ctx.strokeStyle = '#FFFFFF';
    // Dynamically set line width - slightly thinner for premium look
    ctx.lineWidth = Math.max(0.8, scale * court.lineWidth * 0.85);
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.globalAlpha = 0.95; // Crisp but not piercing

    // --- Draw all SOLID lines in one single operation for perfect joins ---
    ctx.beginPath();

    // 1. Outer boundary (doubles)
    ctx.rect(0, 0, courtPixelWidth, courtPixelHeight);

    // 2. Singles sidelines (inner verticals, full length)
    const singlesSidelineX1 = scale * court.singlesOffsetFromEdge;
    const singlesSidelineX2 = courtPixelWidth - (scale * court.singlesOffsetFromEdge);
    ctx.moveTo(singlesSidelineX1, 0);
    ctx.lineTo(singlesSidelineX1, courtPixelHeight);
    ctx.moveTo(singlesSidelineX2, 0);
    ctx.lineTo(singlesSidelineX2, courtPixelHeight);

    // 3. Long service lines for doubles (0.76m from back)
    const longServiceLineYTop = scale * court.longServiceFromBack;
    const longServiceLineYBot = courtPixelHeight - (scale * court.longServiceFromBack);
    ctx.moveTo(0, longServiceLineYTop);
    ctx.lineTo(courtPixelWidth, longServiceLineYTop);
    ctx.moveTo(0, longServiceLineYBot);
    ctx.lineTo(courtPixelWidth, longServiceLineYBot);

    // 4. Short service lines (net ± 1.98m)
    const halfLength = courtPixelHeight / 2;
    const shortServiceOffset = scale * court.shortServiceFromNet;
    const shortServiceLineYTop = halfLength - shortServiceOffset;
    const shortServiceLineYBot = halfLength + shortServiceOffset;
    ctx.moveTo(0, shortServiceLineYTop);
    ctx.lineTo(courtPixelWidth, shortServiceLineYTop);
    ctx.moveTo(0, shortServiceLineYBot);
    ctx.lineTo(courtPixelWidth, shortServiceLineYBot);

    // 5. Center partition lines (match Streamlit segments)
    const halfWidth = courtPixelWidth / 2;
    // Top: boundary to long service
    ctx.moveTo(halfWidth, 0);
    ctx.lineTo(halfWidth, longServiceLineYTop);
    // Top: long service to short service
    ctx.moveTo(halfWidth, shortServiceLineYTop);
    ctx.lineTo(halfWidth, longServiceLineYTop);
    // Bottom: long service to boundary
    ctx.moveTo(halfWidth, longServiceLineYBot);
    ctx.lineTo(halfWidth, courtPixelHeight);
    // Bottom: long service to short service
    ctx.moveTo(halfWidth, longServiceLineYBot);
    ctx.lineTo(halfWidth, shortServiceLineYBot);

    // Render all the solid lines that have been defined above
    ctx.stroke();

    // --- Draw the Net (Dashed White Line) ---
    ctx.save();
    const netY = Math.round(courtPixelHeight / 2) + 0.5;
    ctx.setLineDash([scale * 0.25, scale * 0.25]);

    ctx.beginPath();
    ctx.moveTo(0, netY);
    ctx.lineTo(courtPixelWidth, netY);
    ctx.stroke();

    ctx.restore();


    // Only proceed with player tracking if we have data
    if (!data || data.length === 0) {
      if (showNoDataMessage) {
        ctx.save();
        ctx.fillStyle = 'rgba(255,255,255,0.9)';
        ctx.strokeStyle = 'rgba(0,0,0,0.12)';
        ctx.lineWidth = 1;
        const w = Math.min(courtPixelWidth * 0.7, 260);
        const h = 40;
        const x = (courtPixelWidth - w) / 2;
        const y = 26;
        ctx.beginPath();
        ctx.roundRect(x, y, w, h, 8);
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = '#2d3748';
        ctx.font = '14px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('No tracking points for selected rally', courtPixelWidth / 2, y + 26);
        ctx.restore();
      }
      ctx.restore();
      return;
    }


    // --- Scale player tracking data to fit the court ---
    const scaleToCourt = (value, isX) => {
      if (isX) {
        const clamped = Math.max(0, Math.min(value, court.totalWidth));
        return (clamped / court.totalWidth) * courtPixelWidth;
      } else {
        const clamped = Math.max(0, Math.min(value, court.totalLength));
        return (clamped / court.totalLength) * courtPixelHeight;
      }
    };


    // --- Group by rally and player_side ---
    const rallies = {};
    data.forEach(d => {
      const r = d.RallyID;
      if (!rallies[r]) rallies[r] = { A: [], B: [] };
      if (d.player_side === 'A') rallies[r].A.push(d);
      if (d.player_side === 'B') rallies[r].B.push(d);
    });
    const colors = pathColors;
    const colorForSide = (side) => {
      if (!side) return Object.values(colors)[0] || '#F59E0B';
      const s = String(side).toUpperCase();
      if (colors[side] != null) return colors[side];
      if (s.includes('A')) return colors.A || '#F59E0B';
      if (s.includes('B')) return colors.B || '#06B6D4';
      return '#F43F5E';
    };


    // --- Build shot events per rally ---
    const buildShotEvents = (rallyId, rallyPoints, playersBySide) => {
      if (!rallyPoints || rallyPoints.length === 0) return [];
      const pts = [...rallyPoints].sort((a, b) => Number(a.frame) - Number(b.frame));
      const sidePts = {
        A: (playersBySide.A || []).slice().sort((a, b) => a.frame - b.frame),
        B: (playersBySide.B || []).slice().sort((a, b) => a.frame - b.frame)
      };
      const hitsForRally = Array.isArray(hits)
        ? hits.filter(h => String(h.RallyID) === String(rallyId))
        : [];
      if (hitsForRally.length > 0) {
        const events = [];
        for (const h of hitsForRally) {
          const hitter = String(h.Hitter || '').toUpperCase();
          const frame = Number(h.orig_frame);
          const arrH = sidePts[hitter] || [];
          if (arrH.length === 0) continue;
          let hitIdx = arrH.findIndex(p => Number(p.frame) >= frame);
          if (hitIdx === -1) hitIdx = arrH.length - 1;
          const hit = arrH[hitIdx];
          let commit = null;
          for (let i = hitIdx - 1; i >= 0; i--) {
            commit = arrH[i];
            if (Number(commit.frame) <= Number(hit.frame)) break;
          }
          if (!commit) commit = arrH[0];
          const evt = { commit, hit, side: hitter };
          events.push(evt);
        }
        return events;
      }
      const hasShotFlag = pts.some(p =>
        p.is_shot === true || p.is_shot === '1' || p.is_shot === 1 ||
        p.shot === true || p.shot === '1' || p.shot === 1 ||
        (typeof p.event === 'string' && /hit|serve|smash|shot/i.test(p.event))
      );
      const events = [];
      const tooClose = (a, b) => {
        const dx = Number(a.mid_x) - Number(b.mid_x);
        const dy = Number(a.mid_y) - Number(b.mid_y);
        const dist = Math.hypot(dx, dy);
        const frameGap = Math.abs(Number(a.frame) - Number(b.frame));
        return frameGap < minStarFrameGap || dist < minStarDistanceMeters;
      };
      if (hasShotFlag) {
        let lastKept = null;
        for (let i = 0; i < pts.length; i++) {
          const p = pts[i];
          const isShot = (p.is_shot === true || p.is_shot === '1' || p.is_shot === 1 ||
            p.shot === true || p.shot === '1' || p.shot === 1 ||
            (typeof p.event === 'string' && /hit|serve|smash|shot/i.test(p.event)));
          if (!isShot) continue;
          const prev = i > 0 ? pts[i - 1] : p;
          if (lastKept && (tooClose(p, lastKept.hit))) continue;
          const evt = { commit: prev, hit: p, side: prev.player_side };
          events.push(evt);
          lastKept = evt;
        }
        return events;
      }
      let lastKept = null;
      for (let i = 1; i < pts.length; i++) {
        const prev = pts[i - 1];
        const curr = pts[i];
        if (prev.player_side !== curr.player_side) {
          const evt = { commit: prev, hit: curr, side: prev.player_side };
          if (lastKept && (tooClose(curr, lastKept.hit))) continue;
          events.push(evt);
          lastKept = evt;
        }
      }
      return events;
    };


    // --- Draw paths, markers, and legend ---
    Object.entries(rallies).forEach(([rally, players]) => {
      const rallyPoints = data.filter(d => String(d.RallyID) === String(rally)).sort((a, b) => a.frame - b.frame);
      const shotEvents = buildShotEvents(rally, rallyPoints, players);
      Object.entries(players).forEach(([side, points]) => {
        if (points.length === 0) return;
        const drawArrow = (x1, y1, x2, y2, color) => {
          const angle = Math.atan2(y2 - y1, x2 - x1);
          const size = 6;
          ctx.save();
          ctx.translate(x2, y2);
          ctx.rotate(angle);
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.moveTo(0, 0);
          ctx.lineTo(-size, size * 0.6);
          ctx.lineTo(-size, -size * 0.6);
          ctx.closePath();
          ctx.fill();
          ctx.restore();
        };

        const hitsForRally = Array.isArray(hits)
          ? hits.filter(h => String(h.RallyID) === String(rally))
          : [];
        const sidePtsSorted = points.slice().sort((a, b) => a.frame - b.frame);
        const sideHits = [];
        if (hitsForRally.length > 0) {
          const hitterKey = String(side).toUpperCase();
          const sideHitsRaw = hitsForRally.filter(h => String(h.Hitter || '').toUpperCase() === hitterKey);
          for (const h of sideHitsRaw) {
            const f = Number(h.orig_frame);
            let idx = sidePtsSorted.findIndex(p => Number(p.frame) >= f);
            if (idx === -1) idx = sidePtsSorted.length - 1;
            if (idx >= 0 && sidePtsSorted[idx]) {
              sideHits.push(sidePtsSorted[idx]);
            }
          }
        }

        const color = colorForSide(side);
        // Build movement chain strictly from time-ordered rally points (no hit insertion)
        const maxSamples = 20; // sample 8 points for the movement trail
        const n = sidePtsSorted.length;
        let chain = [];
        if (n >= 2) {
          if (n <= maxSamples) {
            chain = sidePtsSorted.slice();
          } else {
            for (let i = 0; i < maxSamples; i++) {
              const idx = Math.round((i * (n - 1)) / (maxSamples - 1));
              chain.push(sidePtsSorted[idx]);
            }
          }
        } else {
          chain = sidePtsSorted.slice();
        }
        // De-duplicate consecutive identical coordinates to avoid zero-length segments
        chain = chain.filter((pt, i, arr) => {
          if (i === 0) return true;
          const prev = arr[i - 1];
          return !(Number(pt.mid_x) === Number(prev.mid_x) && Number(pt.mid_y) === Number(prev.mid_y));
        });
        ctx.save();
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.setLineDash([]);

        for (let i = 0; i < chain.length - 1; i++) {
          const a = chain[i];
          const b = chain[i + 1];
          const ax = Math.round(scaleToCourt(Number(a.mid_x), true)) + 0.5;
          const ay = Math.round(scaleToCourt(Number(a.mid_y), false)) + 0.5;
          const bx = Math.round(scaleToCourt(Number(b.mid_x), true)) + 0.5;
          const by = Math.round(scaleToCourt(Number(b.mid_y), false)) + 0.5;
          ctx.beginPath();
          ctx.moveTo(ax, ay);
          ctx.lineTo(bx, by);
          ctx.stroke();
          if (i > 0) {
            drawArrow(ax, ay, bx, by, color);
          }
        }

        ctx.restore();

        sideHits.forEach(hp => {
          const hx = scaleToCourt(Number(hp.mid_x), true);
          const hy = scaleToCourt(Number(hp.mid_y), false);
          ctx.save();
          ctx.beginPath();
          ctx.fillStyle = 'yellow';
          ctx.strokeStyle = '#000';
          ctx.lineWidth = 0.6;
          for (let j = 0; j < 5; j++) {
            ctx.lineTo(
              hx + 7 * Math.cos((18 + 72 * j) * Math.PI / 180),
              hy - 7 * Math.sin((18 + 72 * j) * Math.PI / 180)
            );
            ctx.lineTo(
              hx + 3.5 * Math.cos((54 + 72 * j) * Math.PI / 180),
              hy - 3.5 * Math.sin((54 + 72 * j) * Math.PI / 180)
            );
          }
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
          ctx.restore();
        });

        if (points.length > 0) {
          const d = points[0];
          const x = scaleToCourt(Number(d.mid_x), true);
          const y = scaleToCourt(Number(d.mid_y), false);
          ctx.save();
          ctx.beginPath();
          ctx.arc(x, y, 8, 0, 2 * Math.PI);
          ctx.fillStyle = '#22C55E';
          ctx.strokeStyle = '#166534';
          ctx.lineWidth = 2;
          ctx.fill();
          ctx.stroke();
          ctx.restore();
        }
        if (points.length > 0) {
          const d = points[points.length - 1];
          const x = scaleToCourt(Number(d.mid_x), true);
          const y = scaleToCourt(Number(d.mid_y), false);
          ctx.save();
          ctx.strokeStyle = '#000000';
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.moveTo(x - 7, y - 7);
          ctx.lineTo(x + 7, y + 7);
          ctx.moveTo(x + 7, y - 7);
          ctx.lineTo(x - 7, y + 7);
          ctx.stroke();
          ctx.restore();
        }
      });
    });


    if (showLegend) {
      const legendPadding = 8;
      const boxW = 70;
      const boxH = 68;
      const boxX = courtPixelWidth - (boxW + legendPadding);
      const boxY = legendPadding;

      // Rounded panel with subtle shadow for consistency
      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,0.22)';
      ctx.shadowBlur = 8;
      ctx.shadowOffsetY = 2;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
      ctx.beginPath();
      ctx.roundRect(boxX, boxY, boxW, boxH, 10);
      ctx.fill();
      ctx.restore();

      // Hairline stroke
      ctx.save();
      ctx.strokeStyle = 'rgba(0,0,0,0.18)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.roundRect(boxX, boxY, boxW, boxH, 10);
      ctx.stroke();
      ctx.restore();

      // Legend items (compact)
      ctx.save();
      ctx.globalAlpha = 0.95;
      ctx.font = 'bold 10px sans-serif';
      ctx.textBaseline = 'middle';
      let y = boxY + 14;
      const xL = boxX + 12;
      const xT = boxX + 40;
      // Start marker
      ctx.beginPath();
      ctx.fillStyle = '#22C55E';
      ctx.strokeStyle = '#166534';
      ctx.lineWidth = 1.5;
      ctx.arc(xL + 8, y, 5, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = '#0b0b0b';
      ctx.fillText('Start', xT, y);
      y += 14;
      // A path + arrow
      ctx.beginPath();
      ctx.strokeStyle = colors.A;
      ctx.lineWidth = 3;
      ctx.moveTo(xL, y);
      ctx.lineTo(xL + 20, y);
      ctx.stroke();
      ctx.save();
      ctx.translate(xL + 20, y);
      ctx.fillStyle = colors.A;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(-6, 4);
      ctx.lineTo(-6, -4);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
      ctx.fillStyle = '#0b0b0b';
      ctx.fillText('A', xT, y);
      y += 14;
      // B path + arrow
      ctx.beginPath();
      ctx.strokeStyle = colors.B;
      ctx.lineWidth = 3;
      ctx.moveTo(xL, y);
      ctx.lineTo(xL + 20, y);
      ctx.stroke();
      ctx.save();
      ctx.translate(xL + 20, y);
      ctx.fillStyle = colors.B;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(-6, 4);
      ctx.lineTo(-6, -4);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
      ctx.fillStyle = '#0b0b0b';
      ctx.fillText('B', xT, y);
      y += 14;
      // End marker (X)
      ctx.beginPath();
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 1.5;
      ctx.moveTo(xL + 2, y - 4);
      ctx.lineTo(xL + 14, y + 6);
      ctx.moveTo(xL + 14, y - 4);
      ctx.lineTo(xL + 2, y + 6);
      ctx.stroke();
      ctx.fillStyle = '#0b0b0b';
      ctx.fillText('End', xT, y);
      ctx.restore();
    }


    ctx.restore(); // Restore context to pre-translation state
  }, [
    data,
    hits,
    style,
    canvasSize.width,
    canvasSize.height,
    showLegend,
    showTitle,
    pathColors,
    pathStrokeWidth,
    endpointStrokeWidth,
    netColor,
    netLineWidthScale,
    showNoDataMessage,
    pathShadow,
    shadowBlur,
    minStarDistanceMeters,
    minStarFrameGap
  ]);


  const containerStyle = {
    backgroundColor: '#15803d',
    display: 'block',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.15)',
    borderRadius: '1rem',
    border: '1px solid rgba(255,255,255,0.1)',
    width: `${width}px`,
    height: `${height}px`,
    position: 'relative',
    overflow: 'hidden',
    ...style
  };


  return (
    <div ref={containerRef} className={styles.canvasContainer} style={containerStyle}>
      <canvas
        ref={canvasRef}
        width={canvasSize.width}
        height={canvasSize.height}
        className={styles.canvas}
      />
      {trackingLoading && (
        <div className={styles.loading}>
          Loading tracking data...
        </div>
      )}
      {trackingError && (
        <div className={styles.error}>
          {trackingError}
        </div>
      )}
      {(!data || data.length === 0) && showNoDataMessage && !trackingLoading && !trackingError && (
        <div className={styles.noData}>
          No tracking data available
        </div>
      )}
    </div>
  );
});

export default PlayerTrackingCanvas;
